import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { GET_PARENT } from '@/modules/API/parent/parentHandler.ts'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useTranslation } from 'react-i18next'
import styles from './parentNotification.module.scss'
import Title from '@/components/_custom/Title/title'
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { MailOutline, VibrationOutlined } from '@mui/icons-material'
import TableRowPreferences from './NotificationTableRow'
import { UPDATE_ACCOUNT } from '@/modules/API/account/accountHandler'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

const ParentNotification: FunctionComponent = () => {
    const { t } = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [notifAvailabilityMail, setNotifAvailabilityMail] = useState<boolean>(true)
    const [notifAvailabilityPush, setNotifAvailabilityPush] = useState<boolean>(true)

    const [notifReminderMail, setNotifReminderMail] = useState<boolean>(true)
    const [notifReminderPush, setNotifReminderPush] = useState<boolean>(true)

    const [notifBookingMail, setNotifBookingMail] = useState<boolean>(true)
    const [notifBookingPush, setNotifBookingPush] = useState<boolean>(true)

    const [notifEventMail, setNotifEventMail] = useState<boolean>(true)
    const [notifEventPush, setNotifEventPush] = useState<boolean>(true)

    const [notifNewPostPush, setNotifNewPostPush] = useState<boolean>(true)

    const [notifTaskNote, setNotifTaskNote] = useState<boolean>(true)
    const [notifTaskNotePush, setNotifTaskNotePush] = useState<boolean>(true)

    const {
        loading: loadingParent,
        error: errorParent,
        data: dataParent
    } = useQuery(GET_PARENT, {
        fetchPolicy: 'network-only'
    })

    useEffect(() => {
        if (dataParent) {
            setNotifAvailabilityMail(dataParent.getParent.notifAvailabilityMail)
            setNotifAvailabilityPush(dataParent.getParent.notifAvailabilityPush)

            setNotifReminderMail(dataParent.getParent.notifReminderMail)
            setNotifReminderPush(dataParent.getParent.notifReminderPush)

            setNotifBookingMail(dataParent.getParent.notifBookingMail)
            setNotifBookingPush(dataParent.getParent.notifBookingPush)

            setNotifEventMail(dataParent.getParent.notifEventMail)
            setNotifEventPush(dataParent.getParent.notifEventPush)

            setNotifNewPostPush(dataParent.getParent.notifNewPostPush)

            setNotifTaskNote(dataParent.getParent.notifTaskNote)
            setNotifTaskNotePush(dataParent.getParent.notifTaskNotePush)
        }
    }, [dataParent])

    const [updateParent] = useMutation(UPDATE_ACCOUNT, {
        variables: {
            notifAvailabilityMail,
            notifAvailabilityPush,
            notifReminderMail,
            notifReminderPush,
            notifBookingMail,
            notifBookingPush,
            notifEventMail,
            notifEventPush,
            notifNewPostPush,
            notifTaskNote,
            notifTaskNotePush
        },
        onCompleted: (): void => {
            setParentNotifsWasUpdated(false)
            showNotificationSuccess(t('notifUpdate'))
        },
        onError: (): void => {
            setParentNotifsWasUpdated(false)
            showNotificationError(t('notifUpdateError'))
        }
    })

    const [parentNotifsWasUpdated, setParentNotifsWasUpdated] = useState<boolean>(false)

    useEffect(() => {
        if (dataParent && !loadingParent && parentNotifsWasUpdated) updateParent()
    }, [parentNotifsWasUpdated])

    function backToHome(): void {
        navigate('/home')
    }

    return (
        <>
            {loadingParent && <Loading />}
            <div className={styles.PagePosition}>
                <div className={styles.NotificationPlacement}>
                    {dataParent && (
                        <div className={styles.Form}>
                            <Title text={t('myAlerts')} className={styles.Title} />
                            <Table className={styles.TableComponent}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: '60%' }}></TableCell>
                                        <TableCell sx={{ width: '20%' }} align={'center'}>
                                            <MailOutline />
                                        </TableCell>
                                        <TableCell sx={{ width: '20%' }} align={'center'}>
                                            <VibrationOutlined />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRowPreferences
                                        legend={t('availableSlots')}
                                        mailNotification={notifAvailabilityMail}
                                        pushNotification={notifAvailabilityPush}
                                        setNotifMail={setNotifAvailabilityMail}
                                        setNotifPush={setNotifAvailabilityPush}
                                        setParentNotifsWasUpdated={setParentNotifsWasUpdated}
                                    />
                                    <TableRowPreferences
                                        legend={t('bookingSlots')}
                                        mailNotification={notifBookingMail}
                                        pushNotification={notifBookingPush}
                                        setNotifMail={setNotifBookingMail}
                                        setNotifPush={setNotifBookingPush}
                                        setParentNotifsWasUpdated={setParentNotifsWasUpdated}
                                    />
                                    <TableRowPreferences
                                        legend={t('events')}
                                        mailNotification={notifEventMail}
                                        setNotifMail={setNotifEventMail}
                                        setNotifPush={setNotifEventPush}
                                        pushNotification={notifEventPush}
                                        setParentNotifsWasUpdated={setParentNotifsWasUpdated}
                                    />
                                    <TableRowPreferences
                                        legend={t('notifReminder')}
                                        mailNotification={notifReminderMail}
                                        setNotifMail={setNotifReminderMail}
                                        setNotifPush={setNotifReminderPush}
                                        pushNotification={notifReminderPush}
                                        setParentNotifsWasUpdated={setParentNotifsWasUpdated}
                                    />
                                    <TableRowPreferences
                                        legend={t('notifNewPostPush')}
                                        pushNotification={notifNewPostPush}
                                        setNotifPush={setNotifNewPostPush}
                                        setParentNotifsWasUpdated={setParentNotifsWasUpdated}
                                    />
                                    <TableRowPreferences
                                        legend={t('tasksAndNotes')}
                                        mailNotification={notifTaskNote}
                                        setNotifMail={setNotifTaskNote}
                                        setNotifPush={setNotifTaskNotePush}
                                        pushNotification={notifTaskNotePush}
                                        setParentNotifsWasUpdated={setParentNotifsWasUpdated}
                                    />
                                </TableBody>
                            </Table>
                        </div>
                    )}
                    {errorParent && <ErrorMessage error={t('generalError')} />}
                    <ButtonTextIcon type={'return'} onClick={backToHome} parent className={styles.Button} />
                </div>
            </div>
        </>
    )
}

export default ParentNotification
